import {
  CLIENT_GROUP_TYPES,
  POSTAL_ADDRESS_COLUMNS,
  formatFyEnd,
  upperEntityName,
  type ClientGroupType,
  type Company,
  type Contact,
  type ClientGroup,
} from "@somasi/shared";
import { STATUS_LABEL } from "@/lib/companies";
import { parseBool, type ParsedRow } from "@/lib/csv-import";
import type { TemplateColumn } from "@/lib/xlsx-template";

/**
 * Client group vocabulary, and the one column contract its import and export
 * both read.
 *
 * The two halves used to be written out separately — the export's column
 * headings beside the list, the import's beside the importer — and they had
 * drifted: the export wrote the billing address and the type's *label*, the
 * template asked for neither, so a file exported from the list could not be fed
 * back in. Both ends name their columns from {@link CLIENT_GROUP_HEADERS} here,
 * so an export is a valid import file by construction.
 *
 * The group's contacts are export-only. They belong to the contacts table and
 * are edited there, so writing them into a group row would be offering an edit
 * the importer cannot honour; they come out so the file can be shared with
 * someone who needs to know who to call, and are ignored on the way back in.
 */

export const TYPES: { value: ClientGroupType; label: string }[] = [
  { value: "direct", label: "Direct client" },
  { value: "b2b_corporate", label: "B2B corporate services" },
  { value: "intermediary", label: "Intermediary" },
  { value: "individual", label: "Individual owner" },
];

export const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.value, t.label])) as Record<
  ClientGroupType,
  string
>;

/**
 * The type a cell names, or `null` if it names nothing recognisable.
 *
 * Both spellings are accepted because both are in circulation: the export
 * writes the label a person reads ("B2B corporate services") and the template
 * has always asked for the stored value ("b2b_corporate"). Refusing either one
 * would make a file that came out of this app invalid on its way back in.
 */
export function parseClientGroupType(text: string | undefined): ClientGroupType | null {
  const needle = (text ?? "").trim().toLowerCase();
  if (!needle) return null;

  const byValue = CLIENT_GROUP_TYPES.find((t) => t === needle.replace(/[\s-]+/g, "_"));
  if (byValue) return byValue;

  return TYPES.find((t) => t.label.toLowerCase() === needle)?.value ?? null;
}

/** The writable columns, with the heading each one is read and written under. */
export const CLIENT_GROUP_HEADERS = {
  name: "Name",
  client_type: "Type",
  address_line1: "Address line 1",
  address_line2: "Address line 2",
  address_city: "City",
  address_state: "State / Province",
  address_postal_code: "Postal code",
  country: "Country",
  default_payment_terms: "Payment Terms",
  is_active: "Active",
  notes: "Notes",
} as const;

/**
 * What the template says about each column beyond its name.
 *
 * `options` is the point of this: a column backed by an enum gets a dropdown in
 * the template and is refused anything else, so the four types are picked from
 * the same four words the dialog offers rather than typed from memory. They are
 * the *labels*, because those are what the app shows and what the export
 * writes; `parseClientGroupType` also still accepts the stored spelling, so a
 * file written against the old template is not suddenly invalid.
 */
const TEMPLATE_EXTRAS: Partial<
  Record<keyof typeof CLIENT_GROUP_HEADERS, Omit<TemplateColumn, "header">>
> = {
  name: { sample: "Example Group Ltd", width: 28 },
  client_type: { sample: TYPE_LABEL.direct, options: TYPES.map((t) => t.label), width: 24 },
  address_line1: { sample: "1001 Brickell Bay Drive", width: 26 },
  address_line2: { sample: "Suite 3200" },
  address_city: { sample: "Miami" },
  address_state: { sample: "Florida" },
  address_postal_code: { sample: "33131" },
  country: { sample: "United States", width: 20 },
  default_payment_terms: { sample: "Due on receipt" },
  // Yes/No is as fixed a set as the type is, and "TRUE", "1" and a tick are all
  // things people put in a column like this. The importer still reads those, but
  // the template no longer invites them.
  is_active: { sample: "Yes", options: ["Yes", "No"], width: 10 },
  notes: { sample: "", width: 40 },
};

/**
 * The template's columns, in order — and the single place the file's shape is
 * stated. The export's headings and the importer's lookups both come from here,
 * so a column cannot be added to one end and missed at the other.
 */
export const CLIENT_GROUP_TEMPLATE_COLUMNS: TemplateColumn[] = (
  Object.entries(CLIENT_GROUP_HEADERS) as [keyof typeof CLIENT_GROUP_HEADERS, string][]
).map(([key, header]) => ({ header, ...TEMPLATE_EXTRAS[key] }));

/** Headings an import file may carry, in the order the template writes them. */
export const CLIENT_GROUP_IMPORT_HEADERS: string[] = CLIENT_GROUP_TEMPLATE_COLUMNS.map(
  (column) => column.header,
);

/**
 * The export-only contact columns.
 *
 * Four rather than one because they answer different questions: the count is
 * what you scan a sheet for, the addresses and numbers are what a mail merge
 * pastes, and the details column is the one a person reads.
 */
export const CLIENT_GROUP_CONTACT_HEADERS = {
  count: "Group Contacts",
  details: "Contact Details",
  emails: "Contact Emails",
  phones: "Contact Phones",
} as const;

export const CLIENT_GROUP_EXPORT_HEADERS: string[] = [
  ...CLIENT_GROUP_IMPORT_HEADERS,
  ...Object.values(CLIENT_GROUP_CONTACT_HEADERS),
];

/** The template's filled-in row, as a parsed file would hand it back. */
export const CLIENT_GROUP_TEMPLATE_SAMPLE: Record<string, string> = Object.fromEntries(
  CLIENT_GROUP_TEMPLATE_COLUMNS.map((column) => [column.header, column.sample ?? ""]),
);

/** The group fields an export row is built from. */
export type ExportableClientGroup = Pick<
  ClientGroup,
  | "name"
  | "client_type"
  | "country"
  | "default_payment_terms"
  | "is_active"
  | "notes"
  | (typeof POSTAL_ADDRESS_COLUMNS)[number]
>;

/** The contact fields the export-only columns are built from. */
export type ExportableContact = Pick<
  Contact,
  "full_name" | "job_title" | "primary_email" | "primary_phone" | "is_active"
>;

/** The columns a contact's details are read from, so one read covers the export. */
export const CLIENT_GROUP_CONTACT_COLUMNS =
  "id, client_group_id, full_name, job_title, primary_email, primary_phone, is_active";

/**
 * One person on a line: `Jane Doe (CFO) <jane@example.com> +1 555 0100`.
 *
 * Blanks are dropped rather than printed as separators, so a contact with
 * nothing but a name reads as a name and not as a name trailed by punctuation.
 * An inactive contact is marked rather than left out — the file is a record of
 * who is on the account, and silently dropping someone reads as "no contact".
 */
function contactLine(contact: ExportableContact): string {
  const parts = [
    contact.full_name,
    contact.job_title ? `(${contact.job_title})` : "",
    contact.primary_email ? `<${contact.primary_email}>` : "",
    contact.primary_phone ?? "",
    contact.is_active ? "" : "[inactive]",
  ];
  return parts.filter(Boolean).join(" ");
}

const joinCells = (values: (string | null)[]): string =>
  values
    .map((v) => (v ?? "").trim())
    .filter(Boolean)
    .join("; ");

/**
 * One row of the client group export: every writable column, then its contacts.
 *
 * `contacts` is the people attached to the group itself. A company's own
 * contacts are that company's, and they are in the companies export of the
 * group rather than here — a group with 655 companies would otherwise push
 * hundreds of names into one cell.
 */
export function clientGroupExportRow(
  group: ExportableClientGroup,
  contacts: ExportableContact[] = [],
): Record<string, unknown> {
  return {
    [CLIENT_GROUP_HEADERS.name]: group.name,
    [CLIENT_GROUP_HEADERS.client_type]: TYPE_LABEL[group.client_type],
    [CLIENT_GROUP_HEADERS.address_line1]: group.address_line1 ?? "",
    [CLIENT_GROUP_HEADERS.address_line2]: group.address_line2 ?? "",
    [CLIENT_GROUP_HEADERS.address_city]: group.address_city ?? "",
    [CLIENT_GROUP_HEADERS.address_state]: group.address_state ?? "",
    [CLIENT_GROUP_HEADERS.address_postal_code]: group.address_postal_code ?? "",
    [CLIENT_GROUP_HEADERS.country]: group.country ?? "",
    [CLIENT_GROUP_HEADERS.default_payment_terms]: group.default_payment_terms ?? "",
    [CLIENT_GROUP_HEADERS.is_active]: group.is_active ? "Yes" : "No",
    [CLIENT_GROUP_HEADERS.notes]: group.notes ?? "",
    [CLIENT_GROUP_CONTACT_HEADERS.count]: contacts.length,
    [CLIENT_GROUP_CONTACT_HEADERS.details]: contacts.map(contactLine).join("; "),
    [CLIENT_GROUP_CONTACT_HEADERS.emails]: joinCells(contacts.map((c) => c.primary_email)),
    [CLIENT_GROUP_CONTACT_HEADERS.phones]: joinCells(contacts.map((c) => c.primary_phone)),
  };
}

/** The columns the PDF report of the list prints, which is narrower than the file. */
export const CLIENT_GROUP_PDF_COLUMNS = [
  { header: "Name", dataKey: CLIENT_GROUP_HEADERS.name },
  { header: "Type", dataKey: CLIENT_GROUP_HEADERS.client_type },
  { header: "Country", dataKey: CLIENT_GROUP_HEADERS.country },
  { header: "Payment Terms", dataKey: CLIENT_GROUP_HEADERS.default_payment_terms },
  { header: "Active", dataKey: CLIENT_GROUP_HEADERS.is_active },
  { header: "Contacts", dataKey: CLIENT_GROUP_CONTACT_HEADERS.count },
];

/** One row as the importer will write it, with whatever it could not accept. */
export interface ParsedClientGroupRow {
  /** Why the row cannot be imported at all. Non-empty means `draft` is null. */
  errors: string[];
  /** What was changed or assumed to make the row importable. */
  warnings: string[];
  draft: Record<string, unknown> | null;
  /** What to show for the row in a preview. */
  label: string;
}

/**
 * Validates a whole import file rather than a row at a time.
 *
 * Two of the checks are about the file, not the row: a name repeated inside it,
 * and a name already on a stored group. Both produce a second group with the
 * same name rather than an error, because this importer only ever inserts — so
 * they are warnings, and they have to be warnings the operator sees *before*
 * committing, which is why they are computed here and not left to the write.
 */
export function validateClientGroupRows(
  rows: ParsedRow[],
  existingNames: Iterable<string> = [],
): ParsedClientGroupRow[] {
  const taken = new Set([...existingNames].map((n) => n.trim().toLowerCase()));
  const seen = new Set<string>();

  return rows.map((raw) => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Upper-cased here, and not only on the write path, so the preview names
    // the row the way the register will store it — see `upperEntityName`.
    const name = upperEntityName(raw[CLIENT_GROUP_HEADERS.name]) ?? "";
    if (!name) errors.push("Name is required");
    if (name.length > 200) errors.push("Name is longer than 200 characters");

    const key = name.toLowerCase();
    if (key && seen.has(key)) warnings.push("This name appears more than once in the file");
    else if (key && taken.has(key)) warnings.push(`A client group named “${name}” already exists`);
    if (key) seen.add(key);

    /*
      The type is one of four values, so a cell holding a fifth is refused
      rather than quietly turned into "Direct client". Importing it as the
      default was the wrong trade: the row looked imported, read as a direct
      client from then on, and the one person who knew it was meant to be an
      intermediary had no way to tell. The template now offers the four as a
      dropdown, so a typed-in fifth is a mistake worth stopping on.

      A blank cell is different — nobody chose a wrong answer, they left the
      question — so it takes the same default the new-group dialog opens with,
      and says so.
    */
    const rawType = (raw[CLIENT_GROUP_HEADERS.client_type] ?? "").trim();
    const clientType = parseClientGroupType(rawType);
    if (rawType && !clientType) {
      errors.push(`Type “${rawType}” is not one of: ${TYPES.map((t) => t.label).join(", ")}`);
    } else if (!rawType) {
      warnings.push(`No type given — importing as ${TYPE_LABEL.direct}`);
    }

    const rawActive = raw[CLIENT_GROUP_HEADERS.is_active]?.trim();
    const terms = (raw[CLIENT_GROUP_HEADERS.default_payment_terms] ?? "").trim();
    // Only worth saying about a row that will be written: a row being skipped
    // for a missing name does not also need telling what its terms would have
    // defaulted to.
    if (!terms && !errors.length) warnings.push("No payment terms given — defaulting to Due on receipt");

    const draft = errors.length
      ? null
      : {
          name,
          client_type: clientType ?? "direct",
          country: (raw[CLIENT_GROUP_HEADERS.country] ?? "").trim() || null,
          // An address part left blank is "not set", not an empty line to print.
          ...Object.fromEntries(
            POSTAL_ADDRESS_COLUMNS.map((column) => [
              column,
              (raw[CLIENT_GROUP_HEADERS[column]] ?? "").trim() || null,
            ]),
          ),
          default_payment_terms: terms || "Due on receipt",
          // A blank cell is a group nobody said to switch off, so it is active —
          // the same answer the new-group dialog gives.
          is_active: rawActive ? parseBool(rawActive) : true,
          notes: (raw[CLIENT_GROUP_HEADERS.notes] ?? "").trim() || null,
        };

    return { errors, warnings, draft, label: name };
  });
}

/**
 * One company of a group, as its own download writes it.
 *
 * Wider than the columns the group page shows, and wider than the companies
 * list export: this file is the answer to "send me everything you hold on this
 * client's companies", so the service flags and the prose come with it. The
 * group's name is not a column — every row has the same one, and it is in the
 * filename.
 */
export function groupCompanyExportRow(company: Company): Record<string, unknown> {
  return {
    "Legal Name": company.legal_name,
    "Trading Name": company.trading_name ?? "",
    "Registration Number": company.registration_number ?? "",
    Jurisdiction: company.jurisdiction ?? "",
    Status: STATUS_LABEL[company.status],
    "Incorporation Date": company.incorporation_date ?? "",
    "Financial Year-End": formatFyEnd(company.fy_end_md),
    "Payment Terms": company.default_payment_terms ?? "",
    "AFR Applicable": company.afr_applicable ? "Yes" : "No",
    "AFR Due": company.afr_due_date ?? "",
    "Payroll Applicable": company.payroll_applicable ? "Yes" : "No",
    "Bookkeeping Applicable": company.bookkeeping_applicable ? "Yes" : "No",
    "Accounts Finalisation Applicable": company.accounts_finalisation_applicable ? "Yes" : "No",
    "Audit Applicable": company.audit_applicable ? "Yes" : "No",
    "QuickBooks Customer Name": company.quickbooks_customer_name ?? "",
    "Nature of Business": company.nature_of_business ?? "",
    "Business Description": company.business_description ?? "",
    Notes: company.notes ?? "",
  };
}

/** The columns the PDF of a group's companies prints — the on-screen table's. */
export const GROUP_COMPANY_PDF_COLUMNS = [
  { header: "Legal Name", dataKey: "Legal Name" },
  { header: "Registration Number", dataKey: "Registration Number" },
  { header: "Jurisdiction", dataKey: "Jurisdiction" },
  { header: "Incorporated", dataKey: "Incorporation Date" },
  { header: "Status", dataKey: "Status" },
  { header: "AFR Due", dataKey: "AFR Due" },
];

/**
 * `somasi-astride-us-llc-companies` — a filename that says which group it is.
 *
 * Stripped to letters, digits and dashes because the string reaches a
 * `download` attribute and then a filesystem, and "Astride US LLC (BVI)" is
 * not a filename everywhere it might be saved.
 */
export function groupFilenameBase(groupName: string, suffix: string): string {
  const slug = groupName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return `somasi-${slug || "client-group"}-${suffix}`;
}
